import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, FileDown } from "lucide-react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const LUX_BLUE = "#152C5B";
const rupee = (n) => `\u20B9${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function QuoteReceipt() {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [terms, setTerms] = useState("");
  const [store, setStore] = useState({ name: "Digital Yantra", address: "", phone: "", email: "" });
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [q, cfg, st] = await Promise.all([
          api.get(`/quotes/${id}`),
          api.get(`/settings/quote`),
          api.get(`/settings/store`),
        ]);
        setQuote(q.data);
        setTerms(cfg.data.terms || "");
        setStore(st.data || store);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [id]);

  const downloadPdf = async () => {
    if (!printRef.current) return;
    setPdfLoading(true);
    try {
      const canvas = await html2canvas(printRef.current, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = 210, pageH = 297;
      const imgW = pageW;
      const imgH = (canvas.height * imgW) / canvas.width;
      let heightLeft = imgH;
      let position = 0;
      pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
      heightLeft -= pageH;
      while (heightLeft > 0) {
        position -= pageH;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
        heightLeft -= pageH;
      }
      pdf.save(`${(quote.quote_no || "quotation").replace(/\//g, "-")}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      console.error("PDF generation failed:", e);
      toast.error("Could not generate PDF");
    } finally { setPdfLoading(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#0055FF]" /></div>;
  if (!quote) return <div className="p-8">Quotation not found.</div>;

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-6">
      {/* A4 print override (this route only) */}
      <style>{`@media print { @page { size: A4; margin: 14mm; } }`}</style>

      <div className="no-print max-w-4xl mx-auto px-6 mb-4 flex items-center justify-between gap-3">
        <Link to="/quotes">
          <Button variant="outline" className="gap-2" data-testid="back-to-quotes"><ArrowLeft className="w-4 h-4" /> Back</Button>
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadPdf} disabled={pdfLoading} className="gap-2" data-testid="download-quote-pdf-btn">
            {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Download PDF (A4)
          </Button>
        </div>
      </div>

      <div ref={printRef} className="print-area mx-auto max-w-[210mm] bg-white shadow-sm border border-[#E2E8F0] print:shadow-none print:border-0">
        <div className="px-10 py-8 text-black text-[13px] flex flex-col min-h-[273mm]">
          {/* Header */}
          <div className="flex justify-between items-start pb-4 mb-6" style={{ borderBottom: `2px solid ${LUX_BLUE}` }}>
            <div className="min-w-0">
              <div className="text-2xl font-display font-bold tracking-tight" style={{ color: LUX_BLUE }}>{store.name}</div>
              {store.address && <div className="text-[11px] text-[#475569] whitespace-pre-line leading-snug mt-1">{store.address}</div>}
              <div className="text-[11px] text-[#475569] mt-0.5">
                {store.phone && <span>Tel: {store.phone}</span>}
                {store.phone && store.email && <span> · </span>}
                {store.email && <span>{store.email}</span>}
              </div>
            </div>
            <div className="text-right shrink-0 pl-4">
              <div className="text-2xl font-display font-bold uppercase tracking-[0.02em]" style={{ color: LUX_BLUE }}>Quotation</div>
              <div className="font-mono text-sm mt-1">{quote.quote_no}</div>
              <div className="font-mono text-[11px] text-[#475569]">{(quote.created_at || "").slice(0, 10)}</div>
            </div>
          </div>

          {/* Bill to */}
          <div className="mb-6">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#475569] mb-1">Quotation For</div>
            <div className="font-semibold text-[15px]">{quote.customer_name}</div>
            {quote.mobile && <div className="font-mono text-[12px]">{quote.mobile}</div>}
            {quote.email && <div className="text-[12px] text-[#475569]">{quote.email}</div>}
            {quote.customer_gst && <div className="text-[12px]"><span className="text-[#475569]">GSTIN: </span><span className="font-mono">{quote.customer_gst}</span></div>}
          </div>

          {/* Items table */}
          <table className="w-full border-collapse text-[12px] mb-4">
            <thead>
              <tr className="text-left" style={{ backgroundColor: "#EEF2FA" }}>
                <th className="border border-[#CBD5E1] px-2 py-1.5 w-8 text-center">#</th>
                <th className="border border-[#CBD5E1] px-2 py-1.5">Description</th>
                <th className="border border-[#CBD5E1] px-2 py-1.5 text-center w-16">HSN</th>
                <th className="border border-[#CBD5E1] px-2 py-1.5 text-center w-14">Qty</th>
                <th className="border border-[#CBD5E1] px-2 py-1.5 text-right w-24">Rate (Taxable)</th>
                <th className="border border-[#CBD5E1] px-2 py-1.5 text-right w-20">GST 18%</th>
                <th className="border border-[#CBD5E1] px-2 py-1.5 text-right w-24">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(quote.items || []).map((it, i) => {
                const unitTaxable = it.qty ? it.taxable / it.qty : it.taxable;
                return (
                  <tr key={it.inventory_id || `${i}-${it.item_name || it.description || ""}`}>
                    <td className="border border-[#CBD5E1] px-2 py-1.5 text-center">{i + 1}</td>
                    <td className="border border-[#CBD5E1] px-2 py-1.5">
                      {it.item_name && <div className="font-semibold">{it.item_name}</div>}
                      {it.description && <div className="whitespace-pre-line text-[#334155] text-[11px] leading-snug">{it.description}</div>}
                      {!it.item_name && !it.description && <span>—</span>}
                    </td>
                    <td className="border border-[#CBD5E1] px-2 py-1.5 text-center font-mono text-[11px]">{it.hsn || ""}</td>
                    <td className="border border-[#CBD5E1] px-2 py-1.5 text-center">{it.qty}</td>
                    <td className="border border-[#CBD5E1] px-2 py-1.5 text-right font-mono">{rupee(unitTaxable)}</td>
                    <td className="border border-[#CBD5E1] px-2 py-1.5 text-right font-mono">{rupee(it.gst)}</td>
                    <td className="border border-[#CBD5E1] px-2 py-1.5 text-right font-mono">{rupee(it.line_total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end mb-6">
            <table className="text-[12px] w-64">
              <tbody>
                <tr><td className="py-0.5 text-[#475569]">Subtotal (Taxable)</td><td className="py-0.5 text-right font-mono">{rupee(quote.subtotal)}</td></tr>
                <tr><td className="py-0.5 text-[#475569]">GST @ 18%</td><td className="py-0.5 text-right font-mono">{rupee(quote.gst_total)}</td></tr>
                <tr className="font-bold text-[14px]" style={{ borderTop: `1px solid ${LUX_BLUE}`, color: LUX_BLUE }}><td className="py-1">Grand Total</td><td className="py-1 text-right font-mono">{rupee(quote.grand_total)}</td></tr>
              </tbody>
            </table>
          </div>

          {quote.notes && (
            <div className="mb-5">
              <div className="text-[10px] font-mono uppercase tracking-wider text-[#475569]">Notes</div>
              <div className="text-[12px] whitespace-pre-line">{quote.notes}</div>
            </div>
          )}

          {/* Terms — pinned to bottom of page */}
          <div className="mt-auto pt-6">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#475569] mb-1">Terms & Conditions</div>
            <div className="text-[10.5px] text-[#334155] whitespace-pre-line leading-snug">{terms}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
